// src/pages/PromotionManagePage.tsx
import * as React from "react";
import useSWR from "swr";
import Swal from "sweetalert2";
import { Alert, Card, Divider, Pagination, Select, Space, Spin, Typography } from "antd";
import {
  getPromotionListApi,
  createPromotionWithImageApi,
  updatePromotionWithImageApi,
  deletePromotionApi,
  getPromotionDetailApi,
  updatePromotionStatusApi,
  type PromotionListItem,
  type PromotionDetail,
  type PromotionType,
  type PromotionStatus,
  type PromotionCreatePayload,
  type PromotionUpdatePayload,
} from "src/api/promotion/promotionApi";
import PromotionTable from "src/components/promotion/PromotionTable";
import PromotionDetailDialog from "src/components/promotion/PromotionDetailDialog";
import PromotionUpsertDialog from "src/components/promotion/PromotionUpsertDialog";
import CustomButton from "src/components/common/CustomButton";
import { snackbarStore } from "src/store/snackbarStore";

const toast = (message: string, severity: "success" | "error" | "info" | "warning" = "info") =>
  snackbarStore.getState().addSnackbar({ message, severity });

export default function PromotionManagePage() {
  const [promotionType, setPromotionType] = React.useState<PromotionType | "">("");
  const [promotionStatus, setPromotionStatus] = React.useState<PromotionStatus | "">("");
  const [page, setPage] = React.useState(1);
  const [size] = React.useState(20);

  const [detailOpen, setDetailOpen] = React.useState(false);
  const [detailData, setDetailData] = React.useState<PromotionDetail | null>(null);
  const [detailLoading, setDetailLoading] = React.useState(false);

  const [upsertOpen, setUpsertOpen] = React.useState(false);
  const [upsertMode, setUpsertMode] = React.useState<"create" | "edit">("create");
  const [upsertItem, setUpsertItem] = React.useState<PromotionDetail | null>(null);

  const swrKey = React.useMemo(
    () => ["promotion-list", promotionType, promotionStatus, page, size] as const,
    [promotionType, promotionStatus, page, size]
  );

  const { data, isValidating, mutate } = useSWR(
    swrKey,
    async () => {
      const apiPage = Math.max(0, page - 1);
      return await getPromotionListApi({
        promotionType,
        promotionStatus,
        page: apiPage,
        size,
      });
    },
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  const statusCode = data?.statusCode;
  const ok = statusCode === undefined || statusCode === 0 || statusCode === 1;
  const apiErrorMsg = !ok ? data?.message || "프로모션 목록 로드 실패" : null;

  const list = data?.data?.content ?? [];
  const totalPages = Math.max(1, data?.data?.page?.totalPages ?? 1);
  const totalElements = data?.data?.page?.totalElements ?? 0;

  const handleReset = () => {
    setPromotionType("");
    setPromotionStatus("");
    setPage(1);
  };

  const handleSearch = () => {
    setPage(1);
    mutate();
  };

  const handleOpenCreate = () => {
    setUpsertMode("create");
    setUpsertItem(null);
    setUpsertOpen(true);
  };

  const handleViewDetail = async (item: PromotionListItem) => {
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const res = await getPromotionDetailApi(item.promotionId);
      const code = res?.statusCode;
      const okDetail = code === 0 || code === 1;
      if (okDetail && res.data) {
        setDetailData(res.data);
      } else {
        toast(res?.message || "상세 조회 실패", "error");
      }
    } catch (e: any) {
      toast(e?.message || "요청 중 오류가 발생했습니다.", "error");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleEdit = async (item: PromotionListItem) => {
    try {
      const res = await getPromotionDetailApi(item.promotionId);
      const code = res?.statusCode;
      const okDetail = code === 0 || code === 1;
      if (okDetail && res.data) {
        setUpsertMode("edit");
        setUpsertItem(res.data);
        setUpsertOpen(true);
      } else {
        toast(res?.message || "상세 조회 실패", "error");
      }
    } catch (e: any) {
      toast(e?.message || "요청 중 오류가 발생했습니다.", "error");
    }
  };

  const handleDelete = async (item: PromotionListItem) => {
    const result = await Swal.fire({
      title: "삭제 확인",
      text: `"${item.promotionName}"을(를) 삭제하시겠습니까?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "삭제",
      cancelButtonText: "취소",
      confirmButtonColor: "#d33",
    });

    if (!result.isConfirmed) return;

    try {
      const res = await deletePromotionApi(item.promotionId);
      const code = res?.statusCode;
      const okDelete = code === 0 || code === 1;
      if (okDelete) {
        toast("프로모션이 삭제되었습니다.", "success");
        await mutate();
      } else {
        toast(res?.message || "프로모션 삭제 실패", "error");
      }
    } catch (e: any) {
      toast(e?.message || "요청 중 오류가 발생했습니다.", "error");
    }
  };

  const handleCreateSubmit = async (
    payload: PromotionCreatePayload,
    bannerImageFile?: File | null
  ): Promise<boolean> => {
    try {
      const res = await createPromotionWithImageApi(payload, bannerImageFile);
      const code = res?.statusCode;
      const okCreate = code === 0 || code === 1;
      if (okCreate) {
        toast("프로모션이 등록되었습니다.", "success");
        await mutate();
        return true;
      } else {
        toast(res?.message || "프로모션 등록 실패", "error");
        return false;
      }
    } catch (e: any) {
      toast(e?.message || "요청 중 오류가 발생했습니다.", "error");
      return false;
    }
  };

  const handleEditSubmit = async (
    payload: PromotionUpdatePayload,
    bannerImageFile?: File | null
  ): Promise<boolean> => {
    if (!upsertItem) return false;

    try {
      const res = await updatePromotionWithImageApi(
        upsertItem.promotionId,
        payload,
        bannerImageFile
      );
      const code = res?.statusCode;
      const okUpdate = code === 0 || code === 1;
      if (okUpdate) {
        toast("프로모션이 수정되었습니다.", "success");
        await mutate();
        return true;
      } else {
        toast(res?.message || "프로모션 수정 실패", "error");
        return false;
      }
    } catch (e: any) {
      toast(e?.message || "요청 중 오류가 발생했습니다.", "error");
      return false;
    }
  };

  const handleStatusChange = async (item: PromotionListItem, newStatus: PromotionStatus) => {
    try {
      const res = await updatePromotionStatusApi(item.promotionId, newStatus);
      const code = res?.statusCode;
      const okStatus = code === 0 || code === 1;
      if (okStatus) {
        toast("프로모션 상태가 변경되었습니다.", "success");
        await mutate();
      } else {
        toast(res?.message || "상태 변경 실패", "error");
      }
    } catch (e: any) {
      toast(e?.message || "요청 중 오류가 발생했습니다.", "error");
    }
  };

  return (
    <div
      style={{
        padding: 24,
        minWidth: 1000,
        overflowX: "auto",
        maxWidth: "100%",
      }}
    >
      <Card
        styles={{ body: { padding: 20 } }}
        style={{
          borderRadius: 12,
          border: "1px solid #e0e0e0",
          width: "100%",
        }}
      >
        <Typography.Title level={5} style={{ fontWeight: 900, margin: "0 0 16px" }}>
          프로모션 관리
        </Typography.Title>

        <Space size={16} align="center" wrap style={{ marginBottom: 16 }}>
          <Select<PromotionType | "">
            value={promotionType}
            onChange={(value) => setPromotionType(value)}
            options={[
              { label: "전체", value: "" },
              { label: "이벤트", value: "EVENT" },
              { label: "타임딜", value: "TIME_DEAL" },
              { label: "플래시세일", value: "FLASH_SALE" },
              { label: "시즌특가", value: "SEASONAL" },
            ]}
            style={{ minWidth: 150 }}
            placeholder="프로모션 타입"
          />

          <Select<PromotionStatus | "">
            value={promotionStatus}
            onChange={(value) => setPromotionStatus(value)}
            options={[
              { label: "전체", value: "" },
              { label: "초안", value: "DRAFT" },
              { label: "진행중", value: "ACTIVE" },
              { label: "취소됨", value: "CANCELED" },
            ]}
            style={{ minWidth: 150 }}
            placeholder="프로모션 상태"
          />

          <CustomButton variant="contained" onClick={handleSearch} disabled={isValidating}>
            검색
          </CustomButton>
          <CustomButton variant="outlined" onClick={handleReset}>
            초기화
          </CustomButton>

          <div style={{ flex: 1 }} />

          <CustomButton onClick={handleOpenCreate}>프로모션 등록</CustomButton>
        </Space>

        <Divider style={{ margin: "16px 0" }} />

        <Space size={12} align="center" style={{ marginBottom: 12 }}>
          <Typography.Text style={{ fontSize: 13, fontWeight: 800, color: "#666" }}>
            총 {totalElements}건
          </Typography.Text>

          {isValidating ? (
            <Space size={8} align="center">
              <Spin />
              <Typography.Text style={{ fontSize: 13, color: "#666" }}>
                불러오는 중...
              </Typography.Text>
            </Space>
          ) : null}
        </Space>

        {apiErrorMsg ? (
          <Alert type="error" message={apiErrorMsg} style={{ marginBottom: 12 }} />
        ) : null}

        <div style={{ minWidth: 900 }}>
          <PromotionTable
            list={list}
            onViewDetail={handleViewDetail}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onStatusChange={handleStatusChange}
            disabled={isValidating}
          />
        </div>

        <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
          <Pagination
            current={Math.min(page, totalPages)}
            total={totalElements}
            pageSize={size}
            showSizeChanger={false}
            onChange={(nextPage) => setPage(nextPage)}
          />
        </div>
      </Card>

      <PromotionDetailDialog
        open={detailOpen}
        detail={detailData}
        loading={detailLoading}
        onClose={() => {
          setDetailOpen(false);
          setDetailData(null);
        }}
      />

      {upsertMode === "create" ? (
        <PromotionUpsertDialog
          open={upsertOpen}
          mode="create"
          onClose={() => {
            setUpsertOpen(false);
            setUpsertItem(null);
          }}
          onSubmit={handleCreateSubmit}
        />
      ) : upsertItem ? (
        <PromotionUpsertDialog
          open={upsertOpen}
          mode="edit"
          item={upsertItem}
          onClose={() => {
            setUpsertOpen(false);
            setUpsertItem(null);
          }}
          onSubmit={handleEditSubmit}
        />
      ) : null}
    </div>
  );
}
